// Gera o story de um link (dados reais do snapshot do link) com o template
// escolhido ou o padrao do tenant/loja, e oferece o download do JPEG.

import { useState } from "react";
import { Download, Loader2, Wand2 } from "lucide-react";
import { STORY_PRESETS, STORY_CUSTOM_PREFIX, STORY_PRESET_PREFIX } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useLinks } from "@/lib/hooks/useLinks";
import { useRenderStory, type CustomStoryTemplate } from "@/lib/hooks/useStories";

interface Props {
  readonly customTemplates: readonly CustomStoryTemplate[];
  readonly onError: (message: string) => void;
}

/** Baixa a imagem de outra origem (URL assinada do Storage): o atributo
 *  download so vale na mesma origem, entao passa por um blob local. */
export async function downloadImage(url: string, filename: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Não foi possível baixar a imagem.");
  const objectUrl = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}

const SELECT_CLASS = "h-9 w-full rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] px-3 text-sm";

export function TemplateOptions({ customTemplates }: { customTemplates: readonly CustomStoryTemplate[] }): JSX.Element {
  return (
    <>
      <optgroup label="Modelos prontos">
        {STORY_PRESETS.map((p) => (
          <option key={p.key} value={`${STORY_PRESET_PREFIX}${p.key}`}>{p.name}</option>
        ))}
      </optgroup>
      {customTemplates.length > 0 && (
        <optgroup label="Meus templates">
          {customTemplates.map((t) => (
            <option key={t.id} value={`${STORY_CUSTOM_PREFIX}${t.id}`}>{t.name}</option>
          ))}
        </optgroup>
      )}
    </>
  );
}

export function StoryGenerator({ customTemplates, onError }: Props): JSX.Element {
  const { data: links = [] } = useLinks();
  const render = useRenderStory();
  const [linkId, setLinkId] = useState("");
  const [templateRef, setTemplateRef] = useState("");
  const [url, setUrl] = useState<string | null>(null);
  const usable = links.filter((l) => l.title);

  async function generate(): Promise<void> {
    setUrl(null);
    try {
      setUrl(await render.mutateAsync({ linkId, templateRef: templateRef || null }));
    } catch (e) {
      onError(e instanceof Error ? e.message : "Não foi possível gerar o story.");
    }
  }

  return (
    <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_220px]">
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="story-link" variant="field">Oferta (link com dados do produto)</Label>
          <select id="story-link" className={SELECT_CLASS} value={linkId} onChange={(e) => setLinkId(e.target.value)}>
            <option value="">Escolha um link</option>
            {usable.map((l) => (
              <option key={l.id} value={l.id}>{l.title}</option>
            ))}
          </select>
          {usable.length === 0 && <p className="text-xs text-muted-foreground">Gere links com dados do produto (título, preço) em Gerar Link ou pelo encaminhamento.</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="story-template" variant="field">Template</Label>
          <select id="story-template" className={SELECT_CLASS} value={templateRef} onChange={(e) => setTemplateRef(e.target.value)}>
            <option value="">Padrão da loja ou geral</option>
            <TemplateOptions customTemplates={customTemplates} />
          </select>
        </div>
        <Button type="button" disabled={!linkId || render.isPending} onClick={() => void generate()}>
          {render.isPending ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {render.isPending ? "Gerando..." : "Gerar story"}
        </Button>
      </div>
      <div className="flex flex-col items-center gap-2">
        {url ? (
          <>
            <img src={url} alt="Story gerado" className="w-full max-w-[200px] rounded-md border border-border" />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => downloadImage(url, "story-lucralink.jpg").catch((e: unknown) => onError(e instanceof Error ? e.message : "Não foi possível baixar."))}
            >
              <Download size={13} /> Baixar imagem
            </Button>
          </>
        ) : (
          <div className="flex aspect-[9/16] w-full max-w-[200px] items-center justify-center rounded-md border border-dashed border-border text-center text-xs text-muted-foreground">
            O story gerado aparece aqui
          </div>
        )}
      </div>
    </div>
  );
}
